/**
 * useGrant — the grant page's state, derived from one preview response.
 *
 * All of it flows from `/authorize/preview`: the realm decides what is
 * REACHABLE, the user's declines decide what is TAKEN, and everything the page
 * shows is a projection of those two. Nothing here fetches, and nothing here
 * knows which edge it is serving.
 *
 * THE REALM COMES FIRST because a checkbox means nothing outside a ceiling. A
 * grant whose schema the realm doesn't cover is `unavailable` — a different
 * state from `declined`, with a different cause, and the page must not conflate
 * them: one is the person's choice, the other is a fact about their realm.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SCOPELESS_RESOURCES } from '@paradigm/permissions/constants/formA';
import { groupGrants, isScoped } from '../../pages/authorization/grantAxis';
import { buildConsentBlob, canKeep } from './consent';
import { consentFace, faceOffer } from '../../utils/identityFace';
import { dateOf } from '../../nucleus/format';

// Counterparties whose grant does not expire by default: a standing relationship,
// and an app's (or its agent's) contract, which runs until it is changed or
// disconnected. A NEW grant has no date unless someone picks one; an existing
// contract keeps whatever end date it already has.
const LASTING_KINDS = new Set(['user', 'follow', 'group', 'public', 'app', 'sub_entity']);

const idOf = (g) => `${g.resource}:${g.verb}:${g.schema_id || '*'}`;

/** Does this realm, on its own, permit the verb a row asks for?
 *
 *  `edit_content` falls back to edit, ONE-DIRECTIONALLY, exactly as
 *  `checker.has_verb` resolves it.
 *
 *  `nodes:modify` used to be resolved here too — a realm holding it satisfied an
 *  ask for edit or delete. The verb is retired (migration 245) precisely because
 *  that asymmetry made a label disagree with what it permitted, so there is
 *  nothing left to resolve.
 */
const realmAllowsVerb = (realm, g) => {
  const verbs = new Set(realm.verbs || []);
  if (verbs.has(`${g.resource}:${g.verb}`)) return true;
  if (g.resource !== 'nodes') return false;
  return g.verb === 'edit_content' && verbs.has('nodes:edit');
};

/** Does this realm reach the type the row names? A row with no schema axis —
 *  `discovery:friends`, `plugins:execute` — is reached by definition; its
 *  shortfall, if any, is always the verb. */
const realmReachesSchema = (realm, g) =>
  g.all_schemas || !g.schema_id
  || (realm.schema_ids || []).some((id) => String(id) === String(g.schema_id));

/** The one sentence explaining why someone is looking at this again. */
export function whyBanner(preview) {
  const prior = preview?.existing;
  if (!prior) return null;
  const who = preview?.requester?.name || 'This';
  // ONE LINE PER STATE: what happened to this grant before, and when. The rows
  // below mark anything that changed, so the line orients rather than explains.
  //
  // A SECOND LINE ONLY WHERE NOTHING ELSE ON THE PAGE SAYS IT.
  //
  // The rule above — the line orients, the rows explain — holds for `active` and
  // `expand`, where what changed is marked row by row below, and for `expired`,
  // whose consequence is the expiry control's own business (a lapsed term is not
  // offered back, and C10 says so where the term is set).
  //
  // It does not hold for the two states that change WHICH GRANT you end up with,
  // because no row can show that. `preview.py` distinguishes them — revoked is
  // `revoked_at` set, paused is `is_active` false — and both the submit paths
  // fork on exactly that: `consent.js` sends createSharing for a revoked share
  // and updateSharing for a paused one, and `authorization.py` excludes revoked
  // EPs from reuse, so approving mints a new one. Same button, same screen, two
  // different acts, and the page named neither.
  const on = (iso) => (dateOf(iso) ? ` on ${dateOf(iso)}` : '');
  // A RUNTIME ASK IS NOT "ASKING FOR MORE THAN YOU GAVE IT". That sentence
  // belongs to a re-authorization — the app's declaration grew and the page is
  // showing the difference. Here the declaration did not change: the app is
  // already granted and asked mid-use for one thing it could not have declared,
  // like the schema this person just picked. The line says which grant this
  // adds to, and that it adds rather than replaces.
  if (preview?.requester?.runtime) {
    return { tone: 'neutral',
      title: `A runtime request — ${who} asked for this while you were using it. `
        + 'Not a new grant: it adds to the one you already made.' };
  }
  switch (prior.state) {
    case 'expand': {
      const n = (prior.wants_more || []).length;
      return { tone: 'warn',
        title: `${who} is asking for more than you gave it${n ? `: ${n} new, marked below` : ''}.` };
    }
    case 'expired':
      return { tone: 'warn', title: `Contract expired${on(prior.expires_at)}.` };
    case 'paused':
      // NOT "approving resumes it". A pause is also how a failed card is held,
      // and `authorization.py` refuses to un-pause a grant in dunning — pressing
      // Authorize there changes the terms and leaves it paused. The preview does
      // not say which kind of pause this is, so this says only what is true of
      // both: it is the same grant, not a new one.
      return { tone: 'warn', title: 'You paused this.',
        detail: `${who} cannot use it while paused. `
          + 'Approving edits this same grant rather than starting a new one.' };
    case 'revoked':
      // NAMED, not softened to "you stopped this". Revoked is the word the rest
      // of the system uses — the EP's own state, the ledger, the apps list — and
      // this screen was the one place it was paraphrased.
      return { tone: 'warn', title: `You revoked this${on(prior.revoked_at)}.`,
        detail: `${who} has had no access since. Approving starts a NEW grant — `
          + 'it does not restore the old one, and whatever it copied before stays copied.' };
    case 'active':
    default:
      return { tone: 'neutral', title: `Last granted${dateOf(prior.created_at) ? ` ${dateOf(prior.created_at)}` : ''}.` };
  }
}

export function useGrant(preview) {
  const [realmIds, setRealmIds] = useState([]);
  /** The identity chosen ON THIS SCREEN. Null is not "no identity": it means nobody
   *  overrode the ladder, so the realms answer, and then the default. */
  const [faceId, setFaceId] = useState(null);
  /** "Show this app every identity I have" — for apps that are ABOUT identities. */
  const [showAllFaces, setShowAllFaces] = useState(false);
  const [scope, setScope] = useState({});
  /** Chosen row-filter values, per schema: { [schemaId]: { [field]: string[] } }.
   *
   *  EMPTY MEANS "WHATEVER THE ASK SAID", NOT "NOTHING". A person who touches
   *  nothing must land on exactly the grant the app requested — so an empty
   *  selection leaves the declared filter alone rather than replacing it with an
   *  empty `in`, which would grant no rows at all and silently break the app. */
  const [picked, setPicked] = useState({});
  /** WHERE THE OUT-OF-REACH LINES GO — 'shared' (this app's own realm), a realm
   *  id of yours, or 'none' (do not grant them).
   *
   *  It starts UNANSWERED — not at `none` — because a default here is an answer.
   *  Unchecked meant "go ahead without the permissions the app says it needs", and
   *  someone could reach Authorize having never read the question.
   *
   *  It gates the button only when the picked realms reach NOTHING of the ask,
   *  because then it is the one thing that could make this a grant of something.
   *  A partial shortfall does not gate it: see `canConfirm`.
   */
  const [placeIn, setPlaceIn] = useState(null);
  /** WHICH of the picked realms, when there is more than one. Separate from the
   *  ANSWER: `placeIn` is a realm id or 'none', so it cannot also carry which
   *  realm is under consideration while nothing is chosen. */
  const [widenPick, setWidenPick] = useState(null);
  // HOW LONG, BY DEFAULT. A person, a group or the public page is a relationship,
  // not an app session: it does not lapse unless you give it a date (and a dated
  // one is reminded before it ends). An app's contract is the same (owner call,
  // 2026-10-08): it used to get 30 days and lapse without a word. Every
  // friendship used to default to 30 days and end without a word.
  const lastingDefault = LASTING_KINDS.has(preview?.requester?.kind) ? 'never' : '30d';
  const [expiry, setExpiryState] = useState(lastingDefault);
  const expiryTouched = useRef(false);
  const setExpiry = useCallback((v) => { expiryTouched.current = true; setExpiryState(v); }, []);
  // the counterparty is known only once the preview lands: take its default then,
  // unless the person already chose
  useEffect(() => {
    if (!expiryTouched.current) setExpiryState(lastingDefault);
  }, [lastingDefault]);

  // ── coming back: rehydrate, don't restart ────────────────────────────────
  //
  // Someone who chose a realm, declined two things and set an expiry did that
  // work once. Making them redo it is how a re-auth quietly becomes a WIDER
  // grant than the one they held — the fastest path through a form is to accept
  // everything, and a page that discards prior decisions is a page that nudges
  // toward exactly that.
  //
  // Seeded once per grant (keyed on ep_id), so a later render never stamps on
  // an edit the person has since made.
  /* EVERY SELECTION ON THIS SCREEN BELONGS TO ONE OWNER.
   *
   * The consent page can now change whose grant it is minting, and these five
   * pieces of state were written when it could not. A realm id, a face, a scope
   * and a set of picked values are all facts about one graph; carried across a
   * switch they are wrong in two different ways:
   *
   *  - `realmIds` keeps an id the new owner's realm list does not contain, so
   *    `realm` resolves null and Approve is dead with nothing visibly selected.
   *    The seeding effect below cannot rescue it — it only ever ADDS.
   *  - `faceId` is worse, because it is SENT: consent.js ships it as
   *    `profile_node_id`, so a profile node from your personal graph would ride
   *    along on a group's approve.
   *
   * Reset on the owner, not on every preview: a refetch of the SAME owner (a
   * retry, a revalidation) must leave what the person has chosen alone.
   * `seeded` is cleared too, so the new owner's existing grant re-seeds. */
  const seeded = useRef(null);
  const owner = preview?.acting_as?.id ?? preview?.acting_as?.name ?? null;
  const lastOwner = useRef(owner);
  useEffect(() => {
    if (lastOwner.current === owner) return;
    lastOwner.current = owner;
    setRealmIds([]);
    setFaceId(null);
    setShowAllFaces(false);
    setScope({});
    setPicked({});
    expiryTouched.current = false;
    setExpiryState(lastingDefault);
    seeded.current = null;
  }, [owner]);

  useEffect(() => {
    const prior = preview?.existing;
    if (!prior || seeded.current === prior.ep_id) return;
    seeded.current = prior.ep_id;

    // ONLY RESTORE WHAT THE PAGE STILL OFFERS. This group's public grant points
    // at a realm absent from its own list; restoring it left an unresolvable id
    // sitting first in the selection, so `realm` was null forever and the button
    // stayed dead no matter what you clicked. A realm you cannot see is a realm
    // you cannot re-grant.
    const offered = new Set((preview?.realms || []).map((r) => String(r.id)));
    // On a group edge, a contract on the null realm restores as sharing nothing (an
    // empty pick), which is what the page and its button read as "nothing".
    const nothing = new Set(preview?.requester?.kind === 'group'
      ? (preview?.realms || []).filter((r) => r.is_null_realm).map((r) => String(r.id)) : []);
    const restore = (prior.realm_ids?.length ? prior.realm_ids
      : (prior.realm_id ? [prior.realm_id] : []))
      .filter((id) => offered.has(String(id)) && !nothing.has(String(id)));
    if (restore.length) setRealmIds(restore);

    // NOTE: neither declines nor picks are restored, because neither exists.
    // What carries forward is the realms — which is what decided the grant.

    // ONLY A DATE STILL AHEAD OF US. A lapsed grant has no term left to keep,
    // and seeding 'keep' from one renewed the grant into a date that had
    // already passed — which reads as success here and as expired everywhere
    // else. Falling through to the counterparty's default (never, for a person,
    // a group or an app) is the right start for renewing it.
    if (canKeep(prior.expires_at)) setExpiryState('keep');
  }, [preview]);

  /** An app ASKS; a person, group or the public do not. With no ask there is
   *  nothing to decline, so the page swaps its declining surface (C3/C5) for a
   *  picking one (C5P) and the inventory becomes the editable thing. */
  const hasAsk = (preview?.ask?.grants || []).length > 0;

  /** A locked realm is not a choice, so it is not chosen.
   *
   *  "Nothing is pre-selected" is a rule about CHOOSING — it exists so nobody
   *  grants from a ceiling they never picked. When the realm is inherited,
   *  derived or borrowed there is nothing to pick, and withholding the
   *  selection would leave the page permanently unusable rather than careful.
   *  So the lock wins over local state, and setRealmId becomes inert. */
  const lock = preview?.realm_lock || null;
  /** The picked set. A locked realm replaces it entirely — there is nothing to
   *  pick, so local state must not shadow the lock. */
  const pickedRealmIds = lock
    ? (lock.realm_id ? [lock.realm_id] : [])
    : realmIds;
  const effectiveRealmId = pickedRealmIds[0] || null;

  /** The ACT form: one named object, and the realm derived by the server.
   *
   *  A note share picks no realm — the pair's own realm receives it, and that
   *  realm may not exist until the moment of approval. So there is genuinely
   *  nothing left to decide once the page has loaded, and blocking on a realm
   *  the person cannot choose would make the page permanently unusable. */
  const item = preview?.item || null;
  const isDerived = lock?.kind === 'derived';

  const realm = useMemo(() => {
    const all = preview?.realms || [];
    return all.find((r) => r.id === effectiveRealmId)
      // fall back to the first id that resolves, so one stale id cannot veto
      // every good one behind it
      || all.find((r) => pickedRealmIds.includes(r.id))
      || null;
  }, [preview, effectiveRealmId, pickedRealmIds]);

  /** Every realm the user picked. A grant is the UNION of them — that is how
   *  the backend folds multiple realms (`fold_union`), so the page must not
   *  pretend a person can only stand in one at a time. */
  const realms = useMemo(
    () => (preview?.realms || []).filter((r) => pickedRealmIds.includes(r.id)),
    [preview, pickedRealmIds],
  );

  /** The union of what those realms reach. Empty selection = nothing reachable,
   *  which is why the page opens dark and the button unusable. */
  const reachable = useMemo(() => {
    if (!realms.length) return null;
    const out = new Set();
    for (const r of realms) for (const id of r.schema_ids || []) out.add(String(id));
    return out;
  }, [realms]);

  /** What the chosen realms do NOT cover, straight from the server's own diff.
   *
   *  `all_schemas` is true for every non-node grant — `automations:write` has no
   *  schema axis to restrict — so the rule below waved them all through the
   *  moment any realm was chosen. A realm unrestricted on NODES was reporting
   *  that it also granted automations and sub-entity declaration, which it does
   *  not: the same response says so in `missing`, and the coverage chip counted
   *  it correctly (12 of 15) while the rows above it read as fully granted.
   *
   *  Union across realms: covered by one is covered. */
  const uncovered = useMemo(() => {
    if (!realms.length) return null;
    const key = (g) => `${g.resource}:${g.verb}:${g.schema_id || '*'}`;
    const sets = realms.map((r) => new Set((r.missing || []).map(key)));
    return { has: (g) => sets.every((m) => m.has(key(g))) };
  }, [realms]);

  const isAvailable = useCallback(
    (g) => {
      if (!reachable) return false;
      if (uncovered?.has(g)) return false;
      return g.all_schemas || reachable.has(String(g.schema_id));
    },
    [reachable, uncovered],
  );

  /** WHAT THE REALMS GRANT. Nothing else feeds this.
   *
   *  There used to be a `declined` set here — the person could untick a row and
   *  it would stop being granted. That went when the ask became a statement
   *  rather than a control, but the SEEDING of it from a prior grant did not,
   *  so a row the previous visit had not held stayed struck through with no way
   *  to change it — the chip blaming the realm for a decision made last time.
   *
   *  The realm is the grant. A tick is a reading of the realms, and there is no
   *  second input to reconcile it with.
   */
  const isOn = isAvailable;


  /** The whole ask, always. A realm marks rows UNAVAILABLE — it never hides
   *  them, because "this realm cannot give you that" is information the person
   *  needs in order to judge the realm. */
  const buckets = useMemo(
    () => groupGrants(preview?.ask?.grants || []),
    [preview],
  );

  const countsBySchemaId = useMemo(() => Object.fromEntries(
    (preview?.inventory || []).map((r) => [String(r.schema_id), r.count]),
  ), [preview]);

  /** What the realm allows on anything it reaches. With no DLR there is no
   *  per-verb ask, so the realm's own verbs are the grant. */
  const pickVerbs = useMemo(
    () => (realm?.verbs || [])
      .filter((v) => v.startsWith('nodes:'))
      .map((v) => v.slice('nodes:'.length))
      .sort(),
    [realm],
  );

  /** The schema axis of the same grants — what C7 lights. Both halves converge
   *  here, so C7 never learns which kind of edge it is rendering. */
  /** Inventory counts what you HAVE. A type the app asked for and you own none
   *  of was absent from the boundary AND from the verb map, so it could never
   *  light — the row appeared at zero with no verbs, reading as "not granted"
   *  when it was. Every list of types on this page is built from this one. */
  const inventoryPlus = useMemo(() => {
    const have = new Set((preview?.inventory || []).map((r) => String(r.schema_id)));
    const add = [];
    for (const g of preview?.ask?.grants || []) {
      if (g.resource !== 'nodes' || !g.schema_id || !g.schema_name) continue;
      if (have.has(String(g.schema_id))) continue;
      have.add(String(g.schema_id));
      add.push({ schema_id: g.schema_id, schema_name: g.schema_name, count: 0 });
    }
    return [...(preview?.inventory || []), ...add];
  }, [preview]);

  const verbsByType = useMemo(() => {
    const out = {};
    if (!hasAsk) {
      // THE REALM IS THE GRANT. Everything it reaches is shared; there is no
      // second selection, because the realm already made it.
      for (const row of preview?.inventory || []) {
        if (reachable?.has(String(row.schema_id))) out[row.schema_name] = pickVerbs;
      }
      return out;
    }
    for (const g of preview?.ask?.grants || []) {
      if (!isOn(g)) continue;
      // ONLY `nodes` LIVES ON A SCHEMA. `discovery:friends`, `plugins:execute`,
      // `automations:write` and `sub_entity:declare` all arrive with schema_id
      // null, which the branch below reads as "every schema" — so every row in
      // the boundary sprouted EXECUTE · WRITE · FRIENDS · GROUPS · REALMS ·
      // DECLARE. Those are not things you can do TO a schema; they have no
      // schema axis at all, and they are already shown as their own cards.
      if (g.resource !== 'nodes') continue;
      if (g.all_schemas) {
        // UNRESTRICTED MEANS EVERYTHING THE REALM REACHES, not nothing.
        // Skipping these lit zero rows while the grant covered every one of
        // them — the boundary understated the grant, which is the one direction
        // a consent page must never be wrong in.
        for (const row of inventoryPlus) {
          if (!reachable || reachable.has(String(row.schema_id))) {
            (out[row.schema_name] = out[row.schema_name] || []).push(g.verb);
          }
        }
        continue;
      }
      const row = inventoryPlus.find((r) => String(r.schema_id) === String(g.schema_id));
      if (!row) continue;
      (out[row.schema_name] = out[row.schema_name] || []).push(g.verb);
    }
    return out;
  }, [preview, isOn, hasAsk, pickVerbs, reachable, inventoryPlus]);

  const grantedEntries = useMemo(() => {
    if (!hasAsk) {
      return (preview?.inventory || [])
        .filter((r) => reachable?.has(String(r.schema_id)))
        .reduce((n, r) => n + r.count, 0);
    }
    const ids = new Set();
    for (const g of (preview?.ask?.grants || []).filter(isOn)) {
      // Same rule: a non-node grant touches no entries, so it counts none.
      // Letting `plugins:execute` through made the tally claim every entry in
      // the realm was exposed by it.
      if (g.resource !== 'nodes') continue;
      if (g.all_schemas) {
        // Same correction as the boundary: an unrestricted grant counts every
        // entry the realm reaches, not zero.
        for (const row of preview.inventory || []) {
          if (!reachable || reachable.has(String(row.schema_id))) ids.add(String(row.schema_id));
        }
      } else if (g.schema_id) {
        ids.add(String(g.schema_id));
      }
    }
    return [...ids].reduce((n, id) => n + (countsBySchemaId[id] || 0), 0);
  }, [preview, isOn, countsBySchemaId, hasAsk, reachable]);

  /** What you asked for that this realm cannot give.
   *
   *  Was "required grants that are off" — but a DLR item's `required` means
   *  node-requirement cardinality, not permission optionality, so that list was
   *  always empty in practice and blocked nothing. What IS worth surfacing is
   *  the gap between intent and ceiling: rows you left ticked that the chosen
   *  realm does not reach. Those grant nothing, and a page that stays silent
   *  about them lets someone approve a grant they think is wider than it is.
   */
  /** WHY a row is out of reach — named, not merely marked.
   *
   *  IT ONLY EVER EXPLAINS A `false` FROM `isAvailable`. Nothing consults it to
   *  decide what is granted, so a wrong answer here cannot widen or narrow a
   *  grant — it can only mislabel one the page has already refused. That
   *  asymmetry is the whole licence for guessing at all, and it is also why the
   *  fallback is `other` rather than a confident reason: `missing` is the
   *  server's own diff and it knows about narrowings (masks, row filters) that a
   *  realm's verb and schema lists do not show.
   *
   *  Asked PER REALM and then folded, because a grant is the union of them. Two
   *  realms failing a row for two different reasons is `mixed` — there is no one
   *  sentence true of both, and picking either would name a realm that is not
   *  the one standing in the way.
   */
  const blockedBy = useCallback((g) => {
    if (!realms.length) return 'no-realm';
    if (isAvailable(g)) return null;
    const causes = new Set(realms.map((r) => {
      if (!realmAllowsVerb(r, g)) return 'verb';
      if (!realmReachesSchema(r, g)) return 'schema';
      return 'other';
    }));
    return causes.size === 1 ? [...causes][0] : 'mixed';
  }, [realms, isAvailable]);

  /** Ask rows the chosen realms do not reach. The app asked; these realms
   *  cannot answer. Surfaced so nobody approves a grant narrower than they think.
   *
   *  `reason` used to be the constant string 'unavailable' on every row, which
   *  said only what the × already said. The cause is the part a person can act
   *  on: a verb their realm withholds and a type it does not contain are fixed
   *  by different realms, and the page was telling them apart internally
   *  (`uncovered` vs `reachable`) while showing one word for both. */
  const blockers = useMemo(
    () => (preview?.ask?.grants || [])
      .filter((g) => !isAvailable(g))
      .map((g) => ({ grant: g, reason: blockedBy(g) })),
    [preview, isAvailable, blockedBy],
  );

  /** The rows that need somewhere to go. `no-realm` is not one of them: nothing
   *  is out of reach yet when nothing has been added — the page has not asked
   *  the question. */
  /** A CHANGED REALM RETIRES THE ANSWER.
   *
   *  The answer names a realm. Left standing after that realm was deselected, the
   *  page showed an unticked box — `on` compares against the CURRENT target — while
   *  the submit still carried `widen_realm_id` for the realm just taken off, which
   *  would have widened and re-attached a realm the person had removed.
   *
   *  It is also what stops a realm you have never answered for from opening with
   *  the question already settled, which reads as a grant you had approved.
   */
  const realmKey = pickedRealmIds.join('|');
  const answeredFor = useRef(realmKey);
  useEffect(() => {
    if (answeredFor.current === realmKey) return;
    answeredFor.current = realmKey;
    setPlaceIn(null);
    setWidenPick(null);
  }, [realmKey]);

  const homelessKey = (g) => `${g.resource}:${g.verb}:${g.schema_id || '*'}`;
  const homeless = useMemo(() => {
    // NOTHING PICKED MEANS NOTHING IS REACHABLE, so the whole ask is homeless and
    // the offer under the chooser is what gives it a home. `blockers` reports
    // `no-realm` for every row in that state — true, and useless here: it is the
    // reason there is no realm, not a reason a realm refused something.
    if (!pickedRealmIds.length) return preview?.ask?.grants || [];
    return blockers.filter((b) => b.reason && b.reason !== 'no-realm').map((b) => b.grant);
  }, [blockers, pickedRealmIds, preview]);

  /** The realms these lines could be ADDED TO — which is the realms already
   *  picked, and no others.
   *
   *  THE PAGE HAD A SECOND REALM PICKER AND DID NOT NEED ONE. The chooser above
   *  has already been used; "add these to a realm" means the realm sitting in it.
   *  The dropdown was asking which OTHER realm of yours happens to already cover
   *  these rows — a different question, and one nobody came here to answer.
   *
   *  It is also a different act. Attaching another realm leaves everything as it
   *  was; adding to this one GROWS it, for every grant it feeds. That is the
   *  warning the page asks the server about before it offers to go ahead.
   */
  const widenTargets = useMemo(() => (homeless.length ? realms : []), [homeless, realms]);

  /** THE realm the second option is about — one name, held in one place.
   *
   *  This app's own pair realm is never among the choices: it is the FIRST
   *  option, under its own name. Deriving the chosen realm in two places is how
   *  the verdict once came to read "added to Work" over a control saying
   *  "→ Nutrition".
   */
  const widenChoices = useMemo(
    // ANOTHER RECIPIENT'S REALM IS NEVER A PLACE TO PUT THIS. A shared realm is
    // the realm one recipient is alone in; growing it changes what THEY reach,
    // and the server refuses it (REALM_IS_SHARED). `is_shared_realm` is on every
    // realm in the preview for exactly this, and offering one the server will
    // reject is worse than not offering it.
    //
    // AND THE TWO FIXED REALMS ARE NOT EDITABLE FROM HERE EITHER. "No Access"
    // (`is_null_realm`) and "Full Access" (`is_default`) are the platform's own
    // two, seeded at signup and named for what they mean, and what they mean is
    // the whole of their use: No Access is how you share NOTHING — this page
    // picks it by default and prints "Shares nothing" on it — so growing it
    // would quietly turn the empty realm into a sharing one, for every contract
    // filed under it; Full Access is already everything, so "add these to it"
    // offers a move with no effect. Groups have had this invariant all along
    // (`builtin_edit_refusal`, and the preview does not even list a group's
    // built-ins); a person's two were offered because they are marked
    // differently, not because they are different.
    () => widenTargets.filter((r) => !r.is_shared_realm
                                  && !r.is_null_realm
                                  && !r.is_default
                                  && String(r.id) !== String(preview?.pair_realm_id)),
    [widenTargets, preview],
  );
  /** Does the ask carry a verb with no type limit?
   *
   *  `{}` is TOP, not "unscoped", and unioning it into a realm makes that realm
   *  unrestricted for every grant attached to it.
   *
   *  THIS NO LONGER WITHDRAWS THE OPTION, AND IT IS NOT THE AUTHORITY ON WHAT
   *  GOES UNRESTRICTED. Withholding the destination was the only honest move
   *  while the page could say nothing more precise than "this widens Goals" —
   *  but `preview-widening` answers the question exactly, by name, and a
   *  consequence someone can read is a consent question rather than a trap.
   *
   *  What that route returns is `_would_unbound`: the verbs that would CHANGE,
   *  which is a fact about this realm and not about the ask. These rows cannot
   *  know it — a realm already unrestricted on the verb loses no limit — so the
   *  warning and the `widen_unbounded` key are both driven from the route's
   *  answer. This says only that the question may arise, which is what the page
   *  uses to wait for that answer before arming the button.
   *
   *  The cost of the old behaviour was never borne by the unrestricted row. It
   *  was borne by the ordinary ones beside it: placement is a single move, so
   *  one `activity:read` with no limit sent five named types to the pair realm
   *  because they happened to be in the same residue.
   *
   *  ASKED ONLY OF ROWS THAT HAVE A TYPE AXIS TO DROP. An empty schema id means
   *  two different things on this wire, and the distance between them is the
   *  whole question here: `nodes:read` with no schema named is every type you
   *  will ever have, and `discovery:realms` carries none because there is no
   *  such thing as a realm-discovery over one type. Reading the second as the
   *  first withdrew the entire widen option — for the ordinary named types
   *  standing beside it, which would have gone into the realm without trouble —
   *  on account of a yes/no capability that cannot widen anything.
   *
   *  It is the same `.some()` either way, because placement is still ONE move:
   *  the whole residue goes into the realm or none of it does.
   */
  const homelessUnbounded = useMemo(
    // `all_schemas` is the preview's own word for it — `schema_id is None` on the
    // server — so this reads the contract rather than inferring the same fact from
    // the absence of a field.
    () => homeless.some((g) => !SCOPELESS_RESOURCES.includes(g.resource)
                            && (g.all_schemas || !g.schema_id)),
    [homeless],
  );

  const widenTarget = useMemo(
    () => widenChoices.find((r) => String(r.id) === String(widenPick)) || widenChoices[0] || null,
    [widenChoices, widenPick],
  );

  /** Membership by value, not by identity: the ask rows and `homeless` are the
   *  same objects today, and a `.includes` that quietly depended on that would
   *  break the moment either list was mapped. */
  const homelessHas = useCallback((g) => {
    const k = homelessKey(g);
    return homeless.some((h) => homelessKey(h) === k);
  }, [homeless]);

  /** WHAT THE GRANT WILL CONTAIN, which is not the same as what the realms reach.
   *
   *  `isAvailable` answers "can the realms you picked reach this today" — the
   *  question the shortfall explains and the realm chooser acts on. Once the
   *  placement question is answered, the rows it covers ARE going to be granted,
   *  and a page still marking them × while a ticked box says they are being added
   *  contradicts itself: the verdict read "covers 3 of 4" about a grant that would
   *  cover four.
   *
   *  Kept separate rather than folded into `isAvailable` because `blockers` and
   *  the shortfall are derived from that one, and they are about the REALM. If
   *  placement silently made rows available, the shortfall would empty itself and
   *  the checkbox would lose the rows it is asking about.
   */
  const isChecked = useCallback(
    (g) => isAvailable(g) || (!!placeIn && placeIn !== 'none' && homelessHas(g)),
    [isAvailable, placeIn, homelessHas],
  );

  /** THE realm the question is about — one name, held in one place.
   *
   *  The checkbox used to keep its own idea of which realm, so with two picked and
   *  the second chosen from its list, the verdict above read "once these are added
   *  to Work" while the box and the warning under it said "→ Nutrition". Same
   *  question, two answers, because it was derived twice.
   *
   *  Its own pair realm is never among the choices: that is the other state's
   *  offer, under the chooser.
   */
  /** The access those homeless rows amount to — what a realm would grow BY, and
   *  the question the widening preview is asked. */
  const homelessAccess = useMemo(() => buildConsentBlob(homeless), [homeless]);

  return {
    realm,
    realms,
    realmId: effectiveRealmId,
    realmIds: pickedRealmIds,
    /** Single-select convenience, kept for the locked and act forms. */
    setRealmId: lock ? () => {} : (id) => setRealmIds(id ? [id] : []),
    /** Add if absent, remove if present. The null realm (sharing nothing) is
     *  exclusive: choosing it clears the rest, and choosing a realm drops it. */
    toggleRealm: lock ? () => {} : (id) => setRealmIds((prev) => {
      const isNull = (x) => (preview?.realms || []).some((r) => r.is_null_realm && String(r.id) === String(x));
      // on a group, nothing picked IS the null realm; elsewhere it is picked by id
      if (isNull(id)) {
        // AND PRESSING IT AGAIN LETS GO OF IT. Every other row in this list is a
        // toggle; this one answered every click with "selected", so once you had
        // said "share nothing" the only way back was to pick a realm you did not
        // want. It is exclusive, not sticky.
        if (prev.length === 1 && String(prev[0]) === String(id)) return [];
        return preview?.requester?.kind === 'group' ? [] : [id];
      }
      const rest = prev.filter((x) => !isNull(x));
      return rest.includes(id) ? rest.filter((x) => x !== id) : [...rest, id];
    }),
    realmLock: lock,
    isRealmLocked: !!lock,
    /** Who this grant will present, and the control that changes it. The identities
     *  are the OWNER's — on a group edge, the group's — as the preview sends them. */
    identities: preview?.identities || [],
    /** The ones this grant may present: what the chosen realms propose, plus the
     *  default. `identities` stays whole — the count on "every identity" is about
     *  everything you own, not about what is offered here. */
    faceOffer: faceOffer({
      realms: preview?.realms || [],
      realmIds: pickedRealmIds,
      identities: preview?.identities || [],
    }),
    faceId,
    setFaceId: (id) => { setShowAllFaces(false); setFaceId(id); },
    showAllFaces,
    setShowAllFaces: (on) => { setShowAllFaces(on); if (on) setFaceId(null); },
    face: consentFace({
      realms: preview?.realms || [],
      realmIds: pickedRealmIds,
      identities: preview?.identities || [],
      chosenId: faceId,
      showAll: showAllFaces,
    }),
    isDerivedRealm: isDerived,
    /** C2 — whose data is at stake. Null on a personal grant. Comes from the
     *  server, not inferred from the presence of a query param, because the
     *  difference between granting your own data and a group's is exactly the
     *  mistake this line exists to prevent. */
    actingAs: preview?.acting_as || null,
    buckets, isOn, isChecked, isAvailable,
    scope,
    setScopeFor: useCallback((bucket, v) => setScope((s) => ({ ...s, [bucket]: v })), []),
    scopedVerbsOn: useCallback(
      (bucket) => (buckets.find((b) => b.id === bucket)?.cards || [])
        .flatMap((c) => c.grants).filter((g) => isScoped(g) && isOn(g))
        .map((g) => g.verb).filter((v, i, a) => a.indexOf(v) === i).sort(),
      [buckets, isOn],
    ),
    expiry, setExpiry,
    /** The values chosen for one schema's filterable field. */
    pickedTypes: useCallback(
      (schemaId, field) => picked[String(schemaId)]?.[field] || [], [picked]),
    /** Add if absent, remove if present — the same shape as toggleRealm. */
    toggleType: useCallback((schemaId, field, value) => setPicked((prev) => {
      const key = String(schemaId);
      const cur = prev[key]?.[field] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [key]: { ...(prev[key] || {}), [field]: next } };
    }), []),
    /** Stamp the chosen values onto the grants about to be submitted.
     *
     *  Applied at SUBMIT rather than held on the grant objects, because
     *  `preview.ask.grants` is server state the page re-derives from; mutating
     *  it would make the displayed ask and the stored consent two views of one
     *  object that can drift. This returns copies. */
    /** WHERE THE OUT-OF-REACH LINES GO, and what the page needs to say about it. */
    placeIn,
    setPlaceIn,
    homeless,
    widenTargets,
    widenTarget,
    widenChoices,
    homelessUnbounded,
    /** Choosing a different realm moves the ANSWER with it when that option is
     *  the one selected — or the control would name one realm while the grant
     *  widened another. */
    chooseWidenRealm: useCallback((id) => {
      setWidenPick(id);
      setPlaceIn((prev) => (prev && prev !== 'none' && prev !== 'shared' ? id : prev));
    }, []),
    homelessAccess,
    /** The realm this pair already shares, when there is one — so the page can
     *  say "your realm with X" rather than promising a new one it will not make. */
    pairRealmId: preview?.pair_realm_id || null,
    /** The rows placement will carry into consent. Empty when declining, which
     *  is the only setting that reproduces the old silent behaviour — now as
     *  something chosen. */
    /** Nothing moves until the question is answered, and `none` is an answer
     *  that moves nothing. */
    placedGrants: (!placeIn || placeIn === 'none') ? [] : homeless,
    applyPickedTypes: useCallback((grants) => grants.map((g) => {
      const sel = picked[String(g.schema_id)];
      if (!sel) return g;
      const filter = { ...(g.filter || {}) };
      let touched = false;
      for (const [field, values] of Object.entries(sel)) {
        if (values && values.length) { filter[field] = { in: [...values] }; touched = true; }
      }
      return touched ? { ...g, filter } : g;
    }), [picked]),
    /** Why this page is in front of you. Null on a first grant — a banner that
     *  always shows says nothing. */
    why: whyBanner(preview),
    priorState: preview?.existing?.state || null,
    /** The schema ids the ask actually names — the boundary leads with these
     *  and folds the rest, so five requested types are not buried under
     *  twenty-five that were never mentioned. Null when the ask names none
     *  specifically, which means "no filter". */
    // AN UNRESTRICTED ASK NAMES EVERY TYPE. Filtering by the ids that happen to
    // carry a schema_id folded all 43 types away while `nodes:delete` on
    // all_schemas was granting every one of them — the boundary understating
    // the grant, the one direction this page must never be wrong in.
    askSchemaIds: (preview?.ask?.grants || []).some(
      (g) => g.resource === 'nodes' && g.all_schemas)
      ? null
      : ((preview?.ask?.grants || []).some((g) => g.resource === 'nodes')
        ? [...new Set((preview?.ask?.grants || [])
            .filter((g) => g.resource === 'nodes' && g.schema_id)
            .map((g) => String(g.schema_id)))]
        : null),
    /** THE SHAPE OF THE GRAPH, asked for and answered.
     *
     *  `include_refs` is a scope key, not a verb, so it flattens into no ask row
     *  and none of the machinery above can see it. It needs its own pair of
     *  facts because enforcement ANDs the flag across the ask, the realm and the
     *  consent: a realm that does not carry it is not a narrower yes but a no,
     *  and a page that showed the ask without the answer would promise the links
     *  a grant then withholds.
     *
     *  Wildcard reads are where it matters — a read scoped to schemas keeps its
     *  references — but the ask is stated wherever it is made. */
    /** The ask line's presence: an app ASKS for the shape; a friend, a group or
     *  the public do not ask for anything, so there is no line to draw. */
    asksLinks: !!preview?.ask?.links,
    /** Did the ask name no types at all on some nodes verb? The same fact
     *  `askSchemaIds: null` encodes, said plainly — a heading cannot read a
     *  null as "unrestricted", and two readings of one fact drift. */
    asksAll: (preview?.ask?.grants || []).some(
      (g) => g.resource === 'nodes' && g.all_schemas),
    /** Links the grant will actually carry. Union across realms, like every
     *  other reading of them here.
     *
     *  TWO HALVES, THE SAME RULE AS EVERY OTHER PERMISSION ON THIS PAGE. With
     *  an ask it is `asked for AND the realm carries it` — the AND enforcement
     *  applies anyway. WITHOUT one — a friend, a group, the public — there is
     *  nothing to ask, so it follows the realms alone, exactly as the verbs and
     *  schemas do (`grantsFromRealms`). Leaving that half out was a silent
     *  narrowing: a share from a realm that holds the shape of your graph built
     *  a consent that omitted `include_refs`, and an omission there is enforced
     *  as "mask the references" — so the page promised everything the realm
     *  reaches and handed over a graph with its links stripped. */
    linksOn: realms.some((r) => r.links)
      && (hasAsk ? !!preview?.ask?.links : true),
    inventoryPlus,
    countsBySchemaId, verbsByType, grantedEntries,
    totalEntries: preview?.total_entries || 0,
    blockers,
    /** The cause behind one row's ×, for the mark that sits beside it. */
    blockedBy,
    /** WHICH ROWS ARE NEW SINCE THE GRANT YOU ALREADY HOLD.
     *
     *  `state: 'expand'` is the whole reason someone is looking at this page
     *  again, and the banner has always said "N new, marked below" — while
     *  `wants_more` was read for its LENGTH and nothing else. Nothing was
     *  marked. The page counted the difference, told the reader it had shown
     *  them where it was, and did not.
     *
     *  `wants_more` is built by the same `_triple` as the ask rows, so the two
     *  key identically. */
    /** A schema's face on this page: its icon, and whether it is one of
     *  ofSelf's native schemas rather than one an app made for itself. */
    faceOf: (schemaId) => (schemaId && preview?.schema_faces?.[String(schemaId)]) || null,
    isNew: (g) => {
      const wants = preview?.existing?.wants_more;
      if (!wants?.length) return false;
      return wants.some((w) => w.resource === g.resource && w.verb === g.verb
        && String(w.schema_id || '*') === String(g.schema_id || '*'));
    },
    item,
    isAct: !!item,
    // A GRANT OF NOTHING IS NOT A GRANT. Something has to be being granted before
    // the button means anything. This leaned on `required`, which never populated,
    // so an empty grant used to arm it.
    //
    // THERE ARE TWO WAYS TO HAVE SOMETHING, and either will do: the realms reach
    // part of the ask, or the out-of-reach rows have been given a home. The second
    // needs no realm of the person's at all — the narrowest grant this page can
    // make attaches NONE of them and gives the app one realm holding exactly its
    // own ask, and requiring a realm here left that case with a dead button and no
    // way to reach it.
    //
    // IT DOES NOT WAIT FOR THE PLACEMENT ANSWER, except where that answer is the
    // only way to have something. Realms that reach part of the ask already arm
    // it, and the shortfall below is a fact about the grant rather than a question
    // blocking it — the button says "Authorize 1 of 4" and means it. Holding it
    // shut stopped every grant with a shortfall on a question whose answer is
    // usually no. When the realms reach NOTHING, the offer is the only way
    // forward, so there the button genuinely is dead until it is answered.
    //
    // EXCEPT JOINING A GROUP, or sharing one node. What you contribute to a group
    // is optional — the server accepts none and files you under your null realm —
    // so a person who picks nothing is still answering, and a dead button stranded
    // them outside a group they had been invited to.
    canConfirm: (item || preview?.requester?.kind === 'group')
      ? true
      : (hasAsk
        ? ((!!realm && (preview?.ask?.grants || []).some(isOn))
           || (!!placeIn && placeIn !== 'none' && homeless.length > 0))
        : !!realm && (reachable?.size || 0) > 0),
    grantedCount: hasAsk
      ? (preview?.ask?.grants || []).filter(isOn).length
      : (reachable?.size || 0),
    hasAsk, pickVerbs,
    /** Non-node permissions the chosen realms carry. With no DLR these are part
     *  of the grant and were shown nowhere — a realm permitting `plugins:execute`
     *  granted it silently, because the boundary only ever spoke about schemas. */
    realmCapabilities: [...new Set(
      realms.flatMap((r) => (r.verbs || []).filter((v) => !v.startsWith('nodes:'))),
    )].sort(),
    /** With no ask, the types in play are the ones the realms reach. */
    reachedSchemaIds: reachable ? [...reachable] : null,
  };
}

export default useGrant;
